/**
 * UTC-pinned date formatting
 *
 * Every user-facing date in this app is a *publication* date — when a post went
 * out, when a badge was issued, when an activity happened. Those are editorial
 * facts, not local events, so they should read the same for every visitor.
 *
 * Pinning the timezone is also load-bearing for correctness, not just
 * consistency. Next.js server-renders client components, so a bare
 * `toLocaleDateString()` formats against UTC on Vercel and against the viewer's
 * timezone in the browser. When those disagree about the calendar day, React
 * reports a hydration mismatch — Sentry DCYFR-LABS-8, which recurred three
 * times because each fix pinned individual call sites instead of the class.
 *
 * Use these helpers instead of calling `toLocale*` directly. The
 * `dcyfr-local/no-unpinned-date-format` ESLint rule enforces it.
 *
 * @see eslint-local-rules/no-unpinned-date-format.js
 */

/** Accepts anything the app stores a date as. */
export type DateInput = Date | string | number;

/**
 * Named formats, so call sites read as intent rather than as options bags.
 *
 * - `long`     — August 13, 2026
 * - `short`    — Aug 13, 2026
 * - `numeric`  — 8/13/2026
 * - `monthDay` — Aug 13
 * - `monthYear`— August 2026
 * - `weekday`  — Wed, Aug 13, 2026
 * - `dateTime` — Aug 13, 2026, 2:30 PM UTC
 */
export type DateFormat =
  | 'long'
  | 'short'
  | 'numeric'
  | 'monthDay'
  | 'monthYear'
  | 'weekday'
  | 'dateTime';

const FORMATS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  short: { year: 'numeric', month: 'short', day: 'numeric' },
  numeric: { year: 'numeric', month: 'numeric', day: 'numeric' },
  monthDay: { month: 'short', day: 'numeric' },
  monthYear: { year: 'numeric', month: 'long' },
  weekday: { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' },
  dateTime: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  },
};

/** Locale is pinned alongside the timezone — the server has no viewer locale to match. */
const LOCALE = 'en-US';

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Format a date in UTC.
 *
 * @param value - Date, ISO string, or epoch milliseconds
 * @param format - A named format, or explicit Intl options (timeZone is forced to UTC)
 * @returns Formatted date, or an empty string if the input is not a valid date
 *
 * @example
 * formatDate(post.publishedAt, 'long')   // "August 13, 2026"
 * formatDate('2026-08-13T02:00:00Z')     // "Aug 13, 2026" (default: short)
 */
export function formatDate(
  value: DateInput,
  format: DateFormat | Intl.DateTimeFormatOptions = 'short'
): string {
  const date = toDate(value);

  // Callers pass API and MDX frontmatter values straight through; an unparseable
  // date should degrade to empty rather than render "Invalid Date".
  if (Number.isNaN(date.getTime())) return '';

  const options = typeof format === 'string' ? FORMATS[format] : format;

  return date.toLocaleDateString(LOCALE, { ...options, timeZone: 'UTC' });
}

/**
 * Format a date and time in UTC, including the timezone name so the reader can
 * see the value is not in their own zone.
 *
 * @example
 * formatDateTime(activity.timestamp) // "Aug 13, 2026, 2:30 PM UTC"
 */
export function formatDateTime(value: DateInput): string {
  const date = toDate(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString(LOCALE, { ...FORMATS.dateTime, timeZone: 'UTC' });
}

/**
 * Machine-readable `YYYY-MM-DD` in UTC, for `<time dateTime>` and sort keys.
 */
export function formatDateISO(value: DateInput): string {
  const date = toDate(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toISOString().slice(0, 10);
}
